import React, { useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Users, Scissors, Zap, Droplet } from 'lucide-react';
import services from '../data/servicesStatic';
import { useScrollReveal } from '../hooks/useScrollReveal';

const ServicesPage = () => {
  const navigate = useNavigate();

  // Scroll reveal hooks
  const headerReveal = useScrollReveal({ threshold: 0.2 });
  const gridReveal = useScrollReveal({ threshold: 0.1 });

  useLayoutEffect(() => {
    // Ensure page starts at top before paint to avoid footer flash
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  const iconForType = {
    boarding: Users,
    day_care: Users,
    grooming: Scissors,
    training: Zap,
    swimming: Droplet,
  };

  return (
    <div className="min-h-screen visitor-page">
      <Navbar />

      <section className="bg-gradient-to-br from-white via-domigo-pink/10 to-domigo-purple/5 py-16 lg:py-28">
        <div
          ref={headerReveal.ref}
          className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center transition-all duration-1000 ${headerReveal.isVisible
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 translate-y-12'
            }`}
        >
          <div className="inline-flex items-center justify-center mb-6">
            <div className="p-4 bg-gradient-pink rounded-3xl shadow-pink">
              <Users className="text-white" size={48} />
            </div>
          </div>
          <h1 className="text-4xl lg:text-6xl font-bold bg-gradient-to-r from-gray-900 via-domigo-pink to-domigo-purple bg-clip-text text-transparent leading-tight mb-6">
            Our Services
          </h1>
          <p className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Premium care and services for your dog — boarding, daycare, grooming, training, and swimming.
          </p>
        </div>
      </section>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div
          ref={gridReveal.ref}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {services.map((svc, index) => (
            <article
              key={svc.id}
              className={`bg-white border-2 border-gray-100 rounded-2xl shadow-lg hover-glow overflow-hidden flex flex-col transition-all duration-800 ${gridReveal.isVisible
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-12'
                }`}
              style={{
                transitionDelay: gridReveal.isVisible ? `${index * 100}ms` : '0ms'
              }}
            >
              <div className="p-6 flex-1 flex flex-col">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-lg bg-gradient-pink text-white shadow-inner">
                      {React.createElement(iconForType[svc.type] || Users, { className: 'w-6 h-6' })}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{svc.title}</h3>
                      <p className="text-sm text-gray-600 mt-1">{svc.tagline}</p>
                    </div>
                  </div>
                </div>

                <ul className="mt-6 mb-6 space-y-2 flex-1">
                  {svc.bullets.map((b, i) => (
                    <li key={i} className="flex items-start text-gray-700">
                      <span className="w-2 h-2 bg-domigo-pink rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <span className="text-sm">{b}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-4 flex items-center justify-between">
                  <p className="text-sm text-gray-500">{svc.meta}</p>
                  <button
                    onClick={() => navigate('/contact')}
                    className="bg-gradient-pink text-white px-4 py-2 rounded-full text-sm font-semibold hover:opacity-95 transition-all duration-300 btn-smooth cta-pulse"
                  >
                    Contact
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ServicesPage;
